import { IDataStore } from '@/services/dataStore/IDataStore'
import useDataStore from '@/services/dataStore/useDataStore'
import { useReferencesStore } from '@/store/references'
import { BaseEffectReferenceSchema, IBaseEffectReference } from '@lifk/types'
import useSWRV from 'swrv'
import { computed, ref, watch } from 'vue'

interface GetEffectSWRVKey {
  name: string
  effectKey?: string
}
const currentRouteSWRVKey = ref<GetEffectSWRVKey>({
  name: 'effect'
})

const useGetEffect = () => {
  const {
    dataStore,
    dataStoreSWRVKey,
    handleDataStoreError,
    disableValidations
  } = useDataStore()
  const referencesStore = useReferencesStore()

  const effectKey = computed(() =>
    referencesStore.selectedContentType === 'effect'
      ? referencesStore.queryValues?.contentKey
      : undefined
  )
  currentRouteSWRVKey.value.effectKey = effectKey.value

  async function fetchEffect(
    dataStore: IDataStore,
    effectKey: string
  ): Promise<IBaseEffectReference | undefined> {
    try {
      const response = await dataStore.getEffect(effectKey)
      if (disableValidations.value) return response
      return BaseEffectReferenceSchema.parse(response)
    } catch (e) {
      handleDataStoreError(e)
      return undefined
    }
  }

  const result = useSWRV(
    () =>
      `/${currentRouteSWRVKey.value.name}/${dataStoreSWRVKey.value.perspective}/${currentRouteSWRVKey.value.effectKey}`,
    async () => {
      if (currentRouteSWRVKey.value.effectKey) {
        return await fetchEffect(
          dataStore.value,
          currentRouteSWRVKey.value.effectKey
        )
      } else {
        return undefined
      }
    },
    {
      // 30s for adapters to prevent too many API calls to paid CMS's
      dedupingInterval: 30000
    }
  )

  watch(effectKey, () => {
    currentRouteSWRVKey.value.effectKey = effectKey.value
  })

  return result
}

export { useGetEffect }
